import { Component, OnInit, ViewChild } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { MatStepper } from '@angular/material';
import { IncentiveService } from '../create-incentive/create-incentive.service';
import { ToastMessage } from '../toast-message/toast-message.service';
import { DatePipe } from '@angular/common';
import { ToasterService } from '../toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import {Router, ActivatedRoute} from '@angular/router';
import { SharedService } from '../shared/shared.service';
import { Observable } from 'rxjs';
import { LoaderService } from '../loader.service';
import { AlertDialogService } from '../shared/alert-dialog/alert-dialog.service';
import { buildIncentiveApiPayload } from './incentive-payload.builder';
import { AuthzAccessHintService } from '../shared/authz-access-hint/authz-access-hint.service';
import { AuthService } from '../auth.service';
import {
  applyDuplicateNotEditable,
  applyEditImmutableContract as applyEditImmutableContractFlags,
  startOfLocalDay,
} from './create-incentive-edit-locks';


@Component({
  selector: 'app-create-incentive',
  templateUrl: './create-incentive.component.html',
  providers: [ToastMessage, DatePipe]
})
export class CreateIncentiveComponent implements OnInit {
  submittingForm = false;
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  isOptional = false;
  public incentiveData: any = {};
  incentiveId: any;
  defaultValue: any= {};
  formLoaded = false;
  @ViewChild('stepper') stepper: MatStepper;
  state = this.sharedService.STATE.CREATE;
  public authzAccessHint: { message: string; requestUrl: string } | null = null;

  public NOT_EDITABLE = {
    incentiveName: false,
    selectedSegment: false,
    identifier: false,
    incentiveDisplayName: false,
    selectedIncentive: false,
    selectedCity: false,
    selectedService: false,
    selectedShift: false,
    selectedRule: false,
    fromDate: false,
    toDate: false,
    priority: false,

    variables: false,
    timings: false,
    goals: false,
    weeklyDays: false,

    tnc: false,
    smsTemplate: false,
    isHHIncentive: false,
    isMCIncentive: false,
    selectedMicroCorridorId: false,
    incentiveVersion: false,
    payoutType: false,
    active: false,
  };

  constructor(private formBuilder: FormBuilder,
              private incentiveService: IncentiveService,
              private toastMessage: ToastMessage,
              private datePipe: DatePipe,
              public toasterService: ToasterService,
              private router: Router,
              private route: ActivatedRoute,
              private sharedService: SharedService,
              private loaderService: LoaderService,
              private alertDialogService: AlertDialogService,
              private authzAccessHintService: AuthzAccessHintService,
              private authService: AuthService,
              ) {}

  createFormUpdateRule() {
    if (this.state === this.sharedService.STATE.DUPLICATE) {
      applyDuplicateNotEditable(this.NOT_EDITABLE);
      return {};
    }
    if (this.state === this.sharedService.STATE.EDIT) {
      this.NOT_EDITABLE.incentiveVersion = true;
      this.NOT_EDITABLE.payoutType = true;
      this.NOT_EDITABLE.isMCIncentive = true;
      this.NOT_EDITABLE.selectedMicroCorridorId = true;
    }
    const startDate = this.defaultValue.startDate;
    if (!startDate) {
      this.applyEditImmutableContract();
      return {};
    }
    const today = startOfLocalDay(new Date());
    const insentiveStartDate = new Date(this.defaultValue.startDate);
    const incentiveStartDay = startOfLocalDay(insentiveStartDate);
    if (this.defaultValue.incentiveType == 'Adhoc') {
      if (this.defaultValue.active)
        Object.keys(this.NOT_EDITABLE).forEach(fieldName => {
          this.NOT_EDITABLE[fieldName] = true;
        });
      else {
        Object.keys(this.NOT_EDITABLE).forEach(fieldName => {
          this.NOT_EDITABLE[fieldName] = false;
        });
      }
    } else if (this.defaultValue.incentiveType == 'Weekly Fixed'){
      this.NOT_EDITABLE['weeklyDays'] = true;
      this.NOT_EDITABLE['variables'] = true;
      this.NOT_EDITABLE['incentiveName'] = true;
      this.NOT_EDITABLE['selectedIncentive'] = true;
      this.NOT_EDITABLE['selectedShift'] = true;
      this.NOT_EDITABLE['selectedCity'] = true;
      this.NOT_EDITABLE['selectedRule'] = true;
      this.NOT_EDITABLE['selectedService'] = true;
      this.NOT_EDITABLE['fromDate'] = true;
      this.NOT_EDITABLE['priority'] = true;
      this.NOT_EDITABLE['timings'] = true;
    } else {
      // Align with captain-incentive: started when startDate <= today (calendar day).
      const notYetStarted = incentiveStartDay.getTime() > today.getTime();
      if (notYetStarted) {
        Object.keys(this.NOT_EDITABLE).forEach(fieldName => {
          this.NOT_EDITABLE[fieldName] = false;
        });
      } else {
        Object.keys(this.NOT_EDITABLE).forEach(fieldName => {
          this.NOT_EDITABLE[fieldName] = true;
        });
        this.NOT_EDITABLE.toDate = false;
        this.NOT_EDITABLE.active = false;
      }
    }
    this.applyEditImmutableContract();
  }

  /** Started edit: keep SMS fields identical to GET (legacy UI freeze semantics). */
  private readonlySmsSnapshotForEdit(): { smsTemplate: string; messageLocale: string } | undefined {
    if (this.state !== this.sharedService.STATE.EDIT || !this.NOT_EDITABLE.smsTemplate) {
      return undefined;
    }
    const snapshot = this.defaultValue;
    if (!snapshot) {
      return undefined;
    }
    return {
      smsTemplate: snapshot.smsTemplate,
      messageLocale: snapshot.messageLocale,
    };
  }

  /**
   * Version stays locked on edit and duplicate (v1 cannot become v2, and the reverse).
   * On edit, payout type and the MC checkbox stay locked too. The corridor itself stays
   * editable only while the incentive has not started, so a service change can pick a new corridor.
   */
  private applyEditImmutableContract(): void {
    const today = new Date();
    applyEditImmutableContractFlags(
      this.state,
      this.sharedService.STATE.EDIT,
      this.sharedService.STATE.DUPLICATE,
      this.NOT_EDITABLE,
      this.defaultValue,
      today,
    );
  }

  ngOnInit() {
    this.loaderService.openLoading();
    this.loadAccessHint();
    const incentiveId = this.route.snapshot.paramMap.get('incentiveId');
    this.incentiveId = incentiveId;
    const action = this.route.snapshot.paramMap.get('action') || this.sharedService.STATE.CREATE;
    switch (action) {
      case 'edit': {
        this.incentiveService.getIncentive(incentiveId)
          .subscribe((res: any) => {
            this.defaultValue = res;
            this.createFormUpdateRule();
            this.formLoaded = true;
            this.loaderService.closeLoading();
          });
        this.state = this.sharedService.STATE.EDIT;
        break;
      }
      case this.sharedService.STATE.CREATE: {
        this.formLoaded = true;
        this.loaderService.closeLoading();
        break;
      }
      case this.sharedService.STATE.DUPLICATE: {
        this.incentiveService.getIncentive(incentiveId)
          .subscribe((res: any) => {
            this.defaultValue = res;
            applyDuplicateNotEditable(this.NOT_EDITABLE);
            this.formLoaded = true;
            this.loaderService.closeLoading();
          });
        this.state = this.sharedService.STATE.DUPLICATE;
        break;
      }
    }
    this.firstFormGroup = this.formBuilder.group({
      firstCtrl: ['', Validators.required]
    });
    this.secondFormGroup = this.formBuilder.group({
      secondCtrl: ''
    });
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('incentive');
  }

 goForward(stepper: MatStepper) {
    stepper.next();
  }

  goBack(stepper: MatStepper) {
    stepper.previous();
  }


  generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  storeIncentiveInfo(event, type) {
    if (!event) {
      return;
    }
    if (this.submittingForm) {
      return;
    }
    this.submittingForm = true;
    if (event.submit === true) {
      this.loaderService.openLoading();
      void this.submitIncentiveWithActor(event);
      return;
    } else {
      const info = {...this.incentiveData};
      info[type] = event;
      this.incentiveData = info;
      this.submittingForm = false;
    }
  }

  private async submitIncentiveWithActor(event: { active?: boolean }): Promise<void> {
    let actorEmail = '';
    try {
      const user: any = await this.authService.getUserDetails().toPromise();
      actorEmail = (user && user.email) ? user.email : '';
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    const incentiveObj = buildIncentiveApiPayload(this.incentiveData, {
      active: event.active,
      state: this.state,
      duplicateState: this.sharedService.STATE.DUPLICATE,
      editState: this.sharedService.STATE.EDIT,
      datePipe: this.datePipe,
      generateUuid: () => this.generateUuid(),
      actorEmail,
      existingPayoutPolicy:
        this.state === this.sharedService.STATE.EDIT
          ? this.defaultValue.payoutPolicy
          : undefined,
      readonlySmsFromGet: this.readonlySmsSnapshotForEdit(),
    });
    switch (this.state) {
      case this.sharedService.STATE.EDIT: {
        this.incentiveService.editIncentive(this.incentiveId, incentiveObj).subscribe((response: any) => {
          if (response) {
            this.toasterService.showToaster(new Toaster({
              type: ToasterType.SUCCESS,
              message: 'Successfully updated ' +  incentiveObj['incentiveType'] + ' incentive',
            }));
            setTimeout( () => {
              this.submittingForm = false;
              this.router.navigate(['/incentives']);
            }, 1000);
          } else {
            this.submittingForm = false;
            this.toasterService.showToaster(new Toaster({
              type: ToasterType.WARNING,
              message: 'Cannot create ' +  incentiveObj['incentiveType'] +  ' incentive',
            }));
          }
          this.loaderService.closeLoading();
        }, error => {
          this.submittingForm = false;
          this.loaderService.closeLoading();
          const errBody = error && error.error;
          const errMessage = (errBody && errBody.data && errBody.data.message) || (errBody && errBody.message) || '';
          if (errMessage === 'INCENTIVE_NOT_EDITABLE') {
            this.alertDialogService.open({
              title: 'Incentive not editable',
              message: 'Already started incentives can\'t be edited.',
              confirmText: 'OK',
            });
            return;
          }
          this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Error in creating ' +  incentiveObj['incentiveType'] +  ' incentive',
          }));
        });
        break;
      }
      case this.sharedService.STATE.DUPLICATE:
      case this.sharedService.STATE.CREATE: {
        this.incentiveService.createIncentive(incentiveObj).subscribe((response: any) => {
          if (response) {
            this.toasterService.showToaster(new Toaster({
              type: ToasterType.SUCCESS,
              message: 'Successfully created ' +  incentiveObj['incentiveType'] + ' incentive',
            }));
            setTimeout( () => {
              this.submittingForm = false;
              this.router.navigate(['/incentives']);
            }, 500);
          }
          else {
            this.submittingForm = false;
            this.toasterService.showToaster(new Toaster({
              type: ToasterType.WARNING,
              message: 'Cannot create ' +  incentiveObj['incentiveType'] +  ' incentive',
            }));
          }
          this.loaderService.closeLoading();
        }, error => {
          this.submittingForm = false;
           this.loaderService.closeLoading();
           this.toasterService.showToaster(new Toaster({
            type: ToasterType.WARNING,
            message: 'Error in creating ' +  incentiveObj['incentiveType'] +  ' incentive',
          }));
        });
        break;
      }
    }
  }

  moveStepper(event) {
    this.stepper.selectedIndex = event;
  }
}
